import React, { useMemo, useState } from 'react';
import { CheckCircle2, MonitorUp, Smartphone, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type HomeShortcutPromptProps = {
  mode: 'astro' | 'general';
  onShowInstructions?: () => void;
  onSnooze: () => void;
  onNeverShow: () => void;
  onDone: () => void;
};

type ShortcutPlatform = 'ios' | 'android' | 'desktop';

const getShortcutPlatform = (): ShortcutPlatform => {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
};

export default function HomeShortcutPrompt({
  mode,
  onShowInstructions,
  onSnooze,
  onNeverShow,
  onDone,
}: HomeShortcutPromptProps) {
  const { t } = useTranslation();
  const [showInstructions, setShowInstructions] = useState(false);
  const platform = useMemo(getShortcutPlatform, []);

  const isAstro = mode === 'astro';
  const title = isAstro
    ? t('chat.home_shortcut.astro_title', 'Open your chart faster')
    : t('chat.home_shortcut.general_title', 'Open Sarvalok faster');
  const body = isAstro
    ? t(
        'chat.home_shortcut.astro_body',
        'Add a shortcut so your chart, daily timing, and Sarvalok are one tap away.',
      )
    : t(
        'chat.home_shortcut.general_body',
        'Add a shortcut to your home screen so Sarvalok is one tap away.',
      );

  const platformTitle =
    platform === 'ios'
      ? t('chat.home_shortcut.ios_title', 'On iPhone or iPad')
      : platform === 'android'
        ? t('chat.home_shortcut.android_title', 'On Android')
        : t('chat.home_shortcut.desktop_title', 'On desktop');

  const platformSteps =
    platform === 'ios'
      ? t('chat.home_shortcut.ios_steps', 'Open this page in Safari, tap Share, then Add to Home Screen.')
      : platform === 'android'
        ? t('chat.home_shortcut.android_steps', 'Open this page in Chrome, tap the three-dot menu, then Add to Home screen.')
        : t('chat.home_shortcut.desktop_steps', 'Use your browser menu to create a shortcut, or bookmark this page for quick access.');

  const handleShowInstructions = () => {
    onShowInstructions?.();
    setShowInstructions(true);
  };

  return (
    <div className="sl-home-shortcut-wrap mx-auto w-full max-w-3xl px-4 pb-4">
      <section
        className="sl-home-shortcut-card rounded-lg border border-slate-200 bg-white/95 p-4 shadow-lg shadow-slate-900/5 backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95 dark:shadow-black/20"
        aria-label={t('chat.home_shortcut.aria_label', 'Home screen shortcut suggestion')}
      >
        <div className="flex items-start gap-3">
          <div className="sl-home-shortcut-icon mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
            {platform === 'desktop' ? <MonitorUp className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold leading-5 text-slate-950 dark:text-white">{title}</h3>
                <p className="mt-1 text-sm leading-5 text-slate-600 dark:text-slate-300">{body}</p>
              </div>
              <button
                type="button"
                onClick={onSnooze}
                className="sl-home-shortcut-close -mr-1 -mt-1 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label={t('chat.home_shortcut.dismiss', 'Dismiss shortcut suggestion')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {!showInstructions ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleShowInstructions}
                  className="sl-home-shortcut-primary inline-flex items-center justify-center rounded-md bg-slate-950 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200 dark:focus-visible:ring-offset-slate-900"
                >
                  {t('chat.home_shortcut.show_how', 'Show me how')}
                </button>
                <button
                  type="button"
                  onClick={onSnooze}
                  className="sl-home-shortcut-secondary inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white dark:focus-visible:ring-offset-slate-900"
                >
                  {t('chat.home_shortcut.not_now', 'Not now')}
                </button>
              </div>
            ) : (
              <div className="sl-home-shortcut-instructions mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/70">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {platformTitle}
                </div>
                <p className="mt-1 text-sm leading-5 text-slate-700 dark:text-slate-200">{platformSteps}</p>
                {isAstro ? (
                  <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    {t('chat.home_shortcut.astro_tip', 'Tip: from Astro mode, the shortcut can bring you back to your chart flow.')}
                  </p>
                ) : null}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={onDone}
                    className="sl-home-shortcut-done inline-flex items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {t('chat.home_shortcut.done', 'Done')}
                  </button>
                  <button
                    type="button"
                    onClick={onNeverShow}
                    className="sl-home-shortcut-never inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-white hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white dark:focus-visible:ring-offset-slate-900"
                  >
                    {t('chat.home_shortcut.never', "Don't show again")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
